import { Link } from 'react-router-dom';
import { LegalShell, LegalSection as Section } from './LegalShell.jsx';

const CONTACT_EMAIL = 'distrx.io@gmail.com';
const LAST_UPDATED = '19 September 2026';

/**
 * Privacy Policy for DistrxPay.
 *
 * Two kinds of person appear in this product and conflating them would be
 * misleading: the *merchant*, who signs up and has an account, and the
 * *customer*, who pays and never does. What we hold about each, and who is
 * responsible for it, is different — so the policy says so plainly.
 */
export function PrivacyPage() {
  return (
    <LegalShell
      title="Privacy Policy"
      updated={LAST_UPDATED}
      intro="What we collect, why, how long we keep it, and what you can ask us to do with it. Written in plain language on purpose."
    >
      <Section n="1" title="Two different people">
        <p>
          This policy covers two groups, and the difference matters:
        </p>
        <ul>
          <li>
            <strong>Merchants</strong> — businesses and their team members who have a
            DistrxPay account. For their data we are the controller.
          </li>
          <li>
            <strong>Customers</strong> — the people who pay a merchant through a
            DistrxPay checkout. They have no account with us. For their data the
            merchant is the controller and we are a processor acting on the merchant’s
            instructions.
          </li>
        </ul>
        <p>
          If you paid a business and want your data changed or deleted, ask that
          business. They can do it, and we will help them.
        </p>
      </Section>

      <Section n="2" title="What we collect from merchants">
        <ul>
          <li>account details: name, email address, business name, country and currency;</li>
          <li>team members you invite, their names, emails and roles;</li>
          <li>your payment provider credentials, encrypted (see section 5);</li>
          <li>your settings: payment methods, branding, custom fields, webhook endpoints, API keys (stored hashed — we cannot show you a secret key again);</li>
          <li>operational records: sign-ins with the IP address, changes to settings, and the activity trail described in section 6.</li>
        </ul>
      </Section>

      <Section n="3" title="What we hold about your customers">
        <p>
          Only what is needed to take a payment and let you recognise the person who made
          it: a phone number, optionally an email address and name, the amount, currency,
          your order reference, the status of the payment, and any custom fields you
          chose to capture.
        </p>
        <p>
          <strong>We never hold card numbers or security codes.</strong> Card details are
          entered into your payment provider’s own code running in the customer’s browser
          and exchanged there for a token; our API rejects any request that contains
          them. What we store of a card is its brand and last four digits, which is what
          a receipt needs.
        </p>
      </Section>

      <Section n="4" title="Why we hold it">
        <ul>
          <li>to provide the Service you asked for — creating payments, showing a checkout, recording outcomes, notifying your systems;</li>
          <li>to keep it secure — rate limiting, account lockout, and an audit record of security-relevant events;</li>
          <li>to prevent and investigate fraud and misuse;</li>
          <li>to meet legal and accounting obligations that apply to payment records;</li>
          <li>to support you when you ask us to look at something.</li>
        </ul>
        <p>
          We do not sell personal data, we do not share it with advertisers, and we do
          not use your payment data or your customers’ data to train machine-learning
          models.
        </p>
      </Section>

      <Section n="5" title="Payment provider credentials">
        <p>
          The API keys and secrets you give us for a provider are encrypted at rest in a
          versioned envelope and are never returned by any endpoint — not to you, not to
          our own support tooling, and not on the platform-administration path, which does
          not load them at all.
        </p>
        <p>
          Disconnecting a provider deletes the stored credentials rather than flagging
          them as inactive.
        </p>
      </Section>

      <Section n="6" title="How long we keep things">
        <ul>
          <li><strong>Payments, refunds and customer records</strong> — for as long as your account is open, and afterwards for as long as tax and accounting law requires us to keep transaction records.</li>
          <li><strong>The activity stream</strong> — every payment event, gateway response, retry, setting change and sign-in — for 90 days, then deleted automatically.</li>
          <li><strong>Webhook delivery records</strong> — including the response your endpoint returned, for a limited period so delivery problems can be diagnosed.</li>
          <li><strong>Security audit records</strong> — longer than the activity stream, because their purpose is to be there after an incident is discovered.</li>
          <li><strong>Checkout sessions</strong> — expire quickly by design and are purged.</li>
        </ul>
      </Section>

      <Section n="7" title="Who else sees it">
        <p>
          We share data only with parties that are part of delivering the Service:
        </p>
        <ul>
          <li>the <strong>payment providers you choose to connect</strong> — we send them what they need to take the payment you asked for. They are independent controllers with their own privacy policies;</li>
          <li>our <strong>infrastructure providers</strong> — hosting, database and email delivery — under contracts that restrict them to processing on our instructions;</li>
          <li><strong>authorities</strong>, where we are legally required to, and only to the extent required.</li>
        </ul>
        <p>
          We will name our current sub-processors on request at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
        </p>
      </Section>

      <Section n="8" title="Security">
        <p>
          Passwords are hashed, secret keys are stored hashed and shown once, provider
          credentials are encrypted, and access is separated by role so that a member of
          staff at a till cannot reach the developer surface. Every business’s data is
          scoped by its own account on every query.
        </p>
        <p>
          No system is perfect. If a breach affects your data we will tell you, and any
          regulator we are required to tell, without undue delay.
        </p>
      </Section>

      <Section n="9" title="Your rights">
        <p>
          Depending on where you live you may have the right to access, correct, export,
          restrict or delete your personal data, and to object to some processing. Write
          to <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>{' '}
          and we will respond within the period the law allows.
        </p>
        <p>
          Merchants can already read and export their own data — including customers and
          payments — through the dashboard and the API at any time, without asking us.
        </p>
        <p>
          Some data we cannot delete on request: records of completed payments are kept
          where law requires it, and a security audit record that could be deleted by the
          person it concerns would not be an audit record.
        </p>
      </Section>

      <Section n="10" title="Cookies">
        <p>
          We use a small number of strictly necessary cookies — a session cookie to keep
          you signed in and the protections around it. There is no advertising or
          cross-site tracking on the dashboard or the checkout.
        </p>
      </Section>

      <Section n="11" title="International transfers">
        <p>
          Data may be processed in a country other than yours, including by a payment
          provider you connect. Where that happens we rely on appropriate safeguards,
          such as standard contractual clauses.
        </p>
      </Section>

      <Section n="12" title="Children">
        <p>
          The Service is for businesses. It is not directed at children, and we do not
          knowingly collect data from them.
        </p>
      </Section>

      <Section n="13" title="Changes and contact">
        <p>
          We will update this policy as the product changes and will tell account owners
          about material changes by email. The date at the top always reflects the
          current version.
        </p>
        <p>
          Questions, requests, or a complaint:{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="legal-link">{CONTACT_EMAIL}</a>.
          Our <Link to="/terms" className="legal-link">Terms of Service</Link> sit
          alongside this policy.
        </p>
      </Section>
    </LegalShell>
  );
}

export default PrivacyPage;
